// src/hooks/usePracticeMode.ts
import { useEffect, useRef, useState } from "react";

/** Shape emitted by PracticeMicPanel on a processed utterance */
export type PracticeResult = {
  rawText: string;
  top?: {
    boardId: string;
    boardTitle: string;
    triggerText: string;
    stage: "strict" | "substring" | "tail" | "anchor" | "fuzzy" | "semantic";
    latencyMs: number;
  };
};

/**
 * Encapsulates Practice Mic + typed parity state:
 * - mode switching
 * - toast messages (typed + mic)
 * - highlight state
 * - auto-center matched card if off-screen
 * - cardRefs for host component to attach
 */
export function usePracticeMode() {
  const [mode, setMode] = useState<"typed" | "mic">("typed");

  // Typed test toast (listens to window event like Live)
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [testKind, setTestKind] = useState<"ok" | "warn" | null>(null);

  useEffect(() => {
    const handler = (e: any) => {
      const d = e?.detail ?? {};
      const isMatch = Boolean(d.matched ?? d.ok ?? d.success ?? d.result);
      if (isMatch) { setTestKind("ok"); setTestMsg(`Matched: ${d.noteTitle ?? "Unknown cue"}`); }
      else { setTestKind("warn"); setTestMsg("No match"); }
      window.setTimeout(() => { setTestMsg(null); setTestKind(null); }, 3000);
    };
    window.addEventListener("cueboard:test-result", handler);
    return () => window.removeEventListener("cueboard:test-result", handler);
  }, []);

  // Mic practice state
  const [practiceTop, setPracticeTop] = useState<PracticeResult["top"] | undefined>(undefined);
  const [practiceToast, setPracticeToast] = useState<{ kind: "ok" | "warn"; msg: string } | null>(null);
  const [highlightedBoardId, setHighlightedBoardId] = useState<string | null>(null);
  const [highlightedTriggerText, setHighlightedTriggerText] = useState<string | null>(null);

  // track card DOM nodes
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // helpers
  function isMostlyInViewport(el: HTMLElement, threshold = 0.6) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const visibleH = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    const visibleW = Math.min(r.right, vw) - Math.max(r.left, 0);
    const area = Math.max(0, visibleH) * Math.max(0, visibleW);
    const full = (r.width || 1) * (r.height || 1);
    return area / full >= threshold;
  }

  function scrollCardIntoView(noteId: string) {
    const el = cardRefs.current[noteId];
    if (!el) return;
    if (!isMostlyInViewport(el)) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  }

  // ephemeral highlight timeout
  const highlightTimer = useRef<number | null>(null);
  useEffect(() => () => { if (highlightTimer.current) window.clearTimeout(highlightTimer.current); }, []);

  const handlePracticeMatch = (res: PracticeResult) => {
    if (res.top) {
      setPracticeTop(res.top);
      setHighlightedBoardId(res.top.boardId);
      setHighlightedTriggerText(res.top.triggerText);
      setPracticeToast({ kind: "ok", msg: `Matched: ${res.top.boardTitle}` });

      // auto-center matched card if off-screen
      scrollCardIntoView(res.top.boardId);

      // clear highlight after 2500ms
      if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
      highlightTimer.current = window.setTimeout(() => {
        setHighlightedBoardId(null);
        setHighlightedTriggerText(null);
      }, 2500);
    } else {
      setPracticeTop(undefined);
      setPracticeToast({ kind: "warn", msg: "No match" });
      setHighlightedBoardId(null);
      setHighlightedTriggerText(null);
    }
    // auto-dismiss toast
    window.setTimeout(() => setPracticeToast(null), 1800);
  };

  return {
    mode, setMode,
    practiceTop, practiceToast,
    highlightedBoardId, highlightedTriggerText,
    testMsg, testKind,
    handlePracticeMatch,
    cardRefs,
  };
}
